/**
 * @file delivery-plan-details.tsx
 * @module app/modules/orders-delivery/delivery-plan-details
 * @description Route plan details screen for order-delivery operations. It loads
 * plan summary data, renders vehicle and order allocations, and supports plan
 * execution/edit flows for generated routes.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { Col, Row } from "react-bootstrap";
import { useEffect, useState } from "react";
import { ordersDelivery, routePlanning } from "../../../../../api/apiEndPoints";
import { routePlanningJSON } from "../../../../../api/apiJSON/routePlanning";
import Method from "../../../../../utils/methods";
import APICallService from "../../../../../api/apiCallService";
import { callApiWithToast } from "../../../../../utils/apiCallWithToast";
import Loader from "../../../../../Global/loader";
import CrossSvg from "../../../../../_admin/assets/media/close.png";
import {
  Manager,
  OrdersDelivery,
  RoutePlanCancelled,
  RoutePlanCompleted,
  RoutePlanFailed,
  RoutePlanned,
  ServiceMember,
} from "../../../../../utils/constants";
import { useLocation, useNavigate } from "react-router-dom";
import GeneratePlan from "../../../../modals/generate-plan";
import { getKey, setKey } from "../../../../../Global/history";
import {
  listOrdersDelivery,
  warehouse as warehouseStoreString,
} from "../../../../../utils/storeString";
import { error, warning } from "../../../../../Global/toast";
import LoadingScreen from '../../../../components/LoadingScreen';
import { usePlanGeneratedPopup } from "../../hooks/use-plan-generated-popup";
import { useAuth } from "../../../auth";
import { useWarehouse } from "../../../../context/WarehouseContext";
import SummaryInfoCard from "../../../_common/components/summary-info-card";
import { formatDeliveryAddress } from "../../../_common/utils/address";
import VehicleRouteCard from "../../components/delivery-plan/vehicle-route-card";
import VehicleWithoutOrdersCard from "../../components/delivery-plan/vehicle-without-orders-card";
import EditRouteOrderModal from "../../components/delivery-plan/edit-route-order-modal";
import {
  addMissingVehiclesToAvailable,
  applyOrderTypeFilter,
  getBackendNoPlanMessage,
  // getRoutePlanSummaryRecord,
  formatWeightTons,
  getVehicleOrdersWeight,
  mergeOrders,
} from "../../utils/delivery-plan-details-helpers";
import { sumAvailableMetrics } from "../../utils/order-metric-values";
import { getRecordFromStructuredResponse } from "../../../_common/utils/api-helper";

const DeliveryRoutesPlanDetails = () => {
  const { selectedWarehouse } = useWarehouse();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const [fetchLoader, setFetchLoader] = useState(false);
  const [loading, setLoading] = useState(false);
  const [routeId, setRouteId] = useState("");
  const [summary, setSummary] = useState<any>({});
  const [isPlanCreate, setIsPlanCreate] = useState<boolean>(false);
  const [planLoading, setPlanLoading] = useState<boolean>(false);
  const [show, setShow] = useState<boolean>(false);
  const [availableVehicle, setAvailableVehicle] = useState<any>(true);
  const [validation, setValidation] = useState<any>(false);
  const [discarded, setDiscarded] = useState<any>(false);
  const [selectedVehicle, setSelectedVehicle] = useState<any>([]);
  const [vehicleId, setVehicleId] = useState<any>();
  const [showGeneratePlan, setShowGeneratePlan] = useState<boolean>(false);
  const [analytic, setAnalytic] = useState<any>([]);
  const [showRouteDetails, setShowRouteDetails] = useState<any>(false);
  const [coordinates, setCoordinates] = useState<any>([]);
  const [vehicleData, setVehicleData] = useState<any>();
  const [vehicleModalShow, setVehicleModalShow] = useState<any>(false);
  const [processableOrders, setProcessableOrders] = useState<any>([]);
  const [editRouteModal, setEditRouteModal] = useState(false);
  const [orders, setOrders] = useState<any>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectLoading, setSelectLoading] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<any>([]);
  const [selected, setSelected] = useState<boolean>(false);
  const [onSaveLoader, setOnSaveLoader] = useState(false);
  const [visible, setVisible] = useState(false);
  const [discardedIds, setDiscardedIds] = useState<any>([]);
  const [orderConfirm, setOrderConfirm] = useState(false);
  const [selectedVehicleIndex, setSelectedVehicleIndex] = useState<any>(-1);
  const [selectedVehicleId, setSelectedVehicleId] = useState<any>(-1);
  const [discardedOrder, setDiscardedOrder] = useState<any>([]);
  const [selectedOrder, setSelectedOrders] = useState<any>(0);
  const [modalVehicleAnalytics, setModalVehicleAnalytics] = useState<any>([]);
  const [filteredOrders, setFilteredOrders] = useState<any>([]);
  const [districtIds, setDistrictIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [orderTypeFilter, setOrderTypeFilter] = useState<string>("all");
  const [routePlanRefKey, setRoutePlanRefKey] = useState("");

  usePlanGeneratedPopup({
    isOpen: Boolean(vehicleModalShow),
    vehicleName: vehicleData?.name,
    routeRefKey: routePlanRefKey,
    onDismiss: () => {
      setVehicleModalShow(false);
      setVehicleData("");
      setRoutePlanRefKey("");
    },
  });

  useEffect(() => {
    (async () => {
      setFetchLoader(true);
      await fetchSummary(undefined, state?.analysisResponse);
      setFetchLoader(false);
    })();
  }, [state?.analysisResponse]);

  useEffect(() => {
    window.onbeforeunload = function (e: any) {
      var message = "Are you sure you want to leave this page?";
      if (typeof e == "undefined") {
        e = window.event;
      }
      if (e) {
        e.returnValue = message;
      }
      return message;
    };
  }, []);

  const dateString = state.date; // Original date string

  // Convert to Date object
  const date = new Date(dateString);

  // Format the date to "ddmmyyyy"
  const formattedDate = `${String(date.getDate()).padStart(2, "0")}-${String(
    date.getMonth() + 1,
  ).padStart(2, "0")}-${date.getFullYear()}`;

  const fetchSummary = async (routeId?: string, analysisResponse?: unknown) => {
    let params: any = {
      date: Method.convertDateToFormat(state.date, "YYYY-MM-DD"),

      orders: state.orderIds,
      vehicles: state?.selectedVehicle,
    };
    if (routeId) {
      params = { ...params, route: routeId };
    }
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    const apiResponse = analysisResponse ?? await callApiWithToast({
      endpoint: routePlanning.planSummary,
      params: routePlanningJSON.listPlanSummary(params),
      module: OrdersDelivery,
      warehouse: selectedWarehouse?.value,
    });
    console.log(apiResponse)
    const response = getRecordFromStructuredResponse(apiResponse);
    let discardedOrders: any = [];

    if (response?.availableVehicles) {
      const vehiclesDistricts = response?.availableVehicles?.map(
        (vehicle: any) =>
          vehicle?.district?.map((d: any) => d?.reference) || [],
      );
      setDistrictIds(vehiclesDistricts);
    }

    if (response?.planData?.plan?.discardedOrders) {
      response?.planData?.plan?.discardedOrders?.map((item: any) => {
        discardedOrders.push(item?.refKey);
      });
    }

    setDiscardedIds([...new Set(discardedOrders)]);
    setDiscarded(!discarded);
    if (response?.status === RoutePlanCompleted) {
      navigate("/orders/review-order-list", {
        state: {
          routeId: response._id,
        },
      });
      state.onHide();
    }
    if (response) {
      let temp = { ...response };
      if (
        response.planData == undefined ||
        response.planData.plan == undefined ||
        response.planData.plan.trucksWithOrders.length == 0
      ) {
        const noPlanMessage = getBackendNoPlanMessage(response);
        if (noPlanMessage) {
          warning(noPlanMessage);
        }
        window.history.back();
        return;
      }
      if (temp.planData.plan.trucksWithOrders.length) {
        let mergeVehicle = await addMissingVehiclesToAvailable(temp);
        temp.availableVehicles = mergeVehicle.availableVehicles;
        temp.availableVehiclesCount = mergeVehicle.availableVehicles.length;
        let mergeOrder = mergeOrders(temp.planData.plan.trucksWithOrders);
        temp.planData.plan.trucksWithOrders = mergeOrder;
        temp.planData.plan.trucksWithOrders =
          temp.planData.plan.trucksWithOrders.filter((truck: any) =>
            truck.orders.some(
              (order: any) =>
                order.isExistingOrder === false ||
                order.partialDelivery === true,
            ),
          );
        if (temp.planData.plan.trucksWithOrders.length === 0) {
          const noPlanMessage = getBackendNoPlanMessage(response);
          if (noPlanMessage) {
            warning(noPlanMessage);
          }
          window.history.back();
          return;
        }
        temp.planData.plan.trucksWithOrders =
          temp.planData.plan.trucksWithOrders.map((val: any) => {
            return {
              ...val,
              isOpen: false,
            };
          });

        let result = temp.planData.plan.trucksWithOrders.reduce(
          (acc: any, current: any) => {
            // Increment truck count
            acc.totalTrucks += 1;

            // Add the number of orders in the current truck
            acc.totalOrders += current.orders.length;

            // Add this truck's weight once; acc.totalWeight is a running total
            // (null when a malformed weight makes it unavailable)
            const truckWeight = getVehicleOrdersWeight(current.orders);
            acc.totalWeight = sumAvailableMetrics([acc.totalWeight, truckWeight]);
            acc.totalConsumedWeight = sumAvailableMetrics([
              acc.totalConsumedWeight,
              truckWeight,
            ]);
            return acc;
          },
          {
            totalTrucks: 0,
            totalConsumedWeight: 0,
            totalOrders: 0,
            totalWeight: 0, // Adding totalWeight to the accumulator
          },
        );

        setAnalytic(result);
        setSummary(temp);
      }
    } else {
      window.history.back();
    }
  };

  /**
   * Fetches the plan summary for editing a specific route, given the selected truck and its orders.
   * @param {any} selectedTruck - The truck for which the plan summary is being fetched.
   * @param {any} truckOrders - The orders assigned to the selected truck.
   * @param {any} index - The index of the selected truck in the list.
   */
  const fetchSummaryForEditRoute = async (
    selectedTruck: any,
    truckOrders: any,
    index: any,
  ) => {
    setOnSaveLoader(true);
    let params: any = {
      date: Method.convertDateToFormat(state.date, "YYYY-MM-DD"),
      orders: truckOrders,
      vehicles: [selectedTruck],
      enforceMaxOrdersPerVehicleLimit: false,
      planSummaryContext: "EDIT_ROUTE_MANUAL_ASSIGNMENT",
    };
    if (routeId) {
      params = { ...params, route: routeId };
    }
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    const apiResponse = await callApiWithToast({
      endpoint: routePlanning.planSummary,
      params: routePlanningJSON.listPlanSummary(params),
      module: OrdersDelivery,
      warehouse: selectedWarehouse?.value || undefined,
    });
    console.log(apiResponse)
    const response = getRecordFromStructuredResponse(apiResponse);
    if (response?.status === RoutePlanCompleted) {
      navigate("/orders/review-order-list", {
        state: {
          routeId: response._id,
        },
      });
      state.onHide();
    }
    if (response) {
      let temp = { ...response };
      let tempSummary = { ...summary };
      if (
        response.planData == undefined ||
        response.planData.plan == undefined ||
        response.planData.plan.trucksWithOrders.length == 0
      ) {
      }
      if (temp.planData.plan.trucksWithOrders.length) {
        let mergeVehicle = await addMissingVehiclesToAvailable(temp);
        temp.availableVehicles = mergeVehicle.availableVehicles;
        temp.availableVehiclesCount = mergeVehicle.availableVehicles.length;
        let mergeOrder = mergeOrders(temp.planData.plan.trucksWithOrders);
        temp.planData.plan.trucksWithOrders = mergeOrder;
        temp.planData.plan.trucksWithOrders =
          temp.planData.plan.trucksWithOrders.filter((truck: any) =>
            truck.orders.some(
              (order: any) =>
                order.isExistingOrder === false ||
                order.partialDelivery === true,
            ),
          );

        temp.planData.plan.trucksWithOrders =
          temp.planData.plan.trucksWithOrders.map((val: any) => {
            return {
              ...val,
              isOpen: false,
            };
          });

        setSelected(false);
        setKey(listOrdersDelivery.selectedOrders, [], true);
        let result = temp?.planData?.plan?.trucksWithOrders?.reduce(
          (acc: any, current: any) => {
            acc.totalTrucks += 1;
            acc.totalConsumedWeight = sumAvailableMetrics([
              acc.totalConsumedWeight,
              current.consumedWeight,
            ]);
            acc.totalOrders += current.orders.length;
            return acc;
          },
          { totalTrucks: 0, totalConsumedWeight: 0, totalOrders: 0 },
        );
        tempSummary.planData.plan.trucksWithOrders[index] =
          temp.planData.plan.trucksWithOrders[0];
        setDiscardedOrder(temp.planData.plan.discardedOrders);
        setSelectedOrders(temp.totalOrders);
        setVisible(true);
        setAnalytic(result);
        setSummary(tempSummary);
        setModalVehicleAnalytics(temp.planData.plan.trucksWithOrders[0]);
        if (!temp.planData.plan.discardedOrders.length) {
          setEditRouteModal(false);
          setSelectedId([]);
        }
      } else {
        setDiscardedOrder(temp?.planData?.plan?.discardedOrders);
        setSelectedOrders(temp.totalOrders);
        setVisible(true);
        if (!temp?.planData?.plan?.discardedOrders?.length) {
          setEditRouteModal(false);
          setSelectedId([]);
        } else {
          setDiscarded(!discarded);
        }
      }
    } else {
      window.history.back();
    }
    setOnSaveLoader(false);
    setOrderConfirm(true);
  };

  const fetchDetails = async (
    status?: any,
    search?: string,
    fromDate?: string,
    toDate?: string,
    ward?: any,
    id?: any,
    districtIds?: any,
  ) => {
    setLoading(true);

    let params: any = {
      sortKey: "_createdAt",
      sortOrder: 1,
      searchTerm: search ? search : "",
      fromDate: fromDate ? fromDate : "",
      toDate: toDate ? toDate : "",
    };
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }

    if (districtIds && Array.isArray(districtIds)) {
      districtIds.forEach((districtId: string, index: number) => {
        params[`district[${index}]`] = districtId;
      });
    }

    if (status && status?.length > 0) {
      status.map((val: any) => {
        params = { ...params, listType: val };
      });
    }

    let endpoint = ordersDelivery.list;
    if (
      currentUser?.role.some(
        (obj: any) => obj.type === Manager || obj.type === ServiceMember,
      )
    ) {
      endpoint = ordersDelivery.listAssistAdmin;
    }

    // Accumulate results from all API calls
    let accumulatedOrders: any[] = [];

    const districtReferences = ward?.map(
      (item: any) => item.district.reference,
    );
    let districts = [...new Set(districtReferences)];
    let apiParams = {
      ...params,
    };
    if (districts.length > 0) {
      districts.map((val: any, index: number) => {
        apiParams = { ...apiParams, ["district[" + index + "]"]: val };
      });
    }
    let apiService = new APICallService(
      endpoint,
      apiParams,
      "",
      "",
      "",
      "",
      OrdersDelivery,
      selectedWarehouse?.value || null,
    );

    let response: any = await apiService.callAPI();

    if (response.records) {
      const existingRefKeys = new Set();
      summary.planData.plan.trucksWithOrders.forEach((vehicle: any) => {
        if (id !== vehicle.postData.vModel.reference)
          vehicle.orders.forEach((order: any) => {
            if (!order.partialDelivery) existingRefKeys.add(order.refKey);
          });
      });

      // Filter out the orders from response.records that already exist in summary.planData.plan.trucksWithOrders
      const filteredOrders = response.records.filter((order: any) => {
        return !existingRefKeys.has(order.refKey); // Only include orders that do not exist in existingRefKeys
      });

      accumulatedOrders = [...accumulatedOrders, ...filteredOrders];
    }

    const removeDuplicates = (orders: any[]) => {
      return orders.filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.refKey === value.refKey),
      );
    };

    if (accumulatedOrders?.length > 0) {
      const uniqueOrders = removeDuplicates(accumulatedOrders);

      // Update state
      setOrders(uniqueOrders);
      setFilteredOrders(uniqueOrders);
      if (accumulatedOrders?.length < params.limit) {
        setTotalRecords(accumulatedOrders.length);
      } else {
        setTotalRecords(accumulatedOrders.length); // You can set total count based on the API response or logic
      }
    } else {
      setOrders([]); // If no orders are found, clear the list
      setTotalRecords(0);
    }

    setLoading(false);
  };

  const handleDefaultCheck = (index: number) => {
    let temp: any = [];
    summary?.planData?.plan?.trucksWithOrders[index].orders.map((val: any) => {
      temp.push(val.postData._id);
    });
    setSelectedId(temp);
  };

  const handleSelectAll = () => {
    let temp: any = [];
    if (selectedId.length !== orders.length) {
      orders.map((val: any) => {
        temp.push(val._id);
      });
    }
    if (temp.length) {
      setValidation(false);
    } else {
      setValidation(true);
    }
    setSelectedId(temp);
  };

  const handleChecked = (id: string) => {
    let temp = [...selectedId];
    if (temp.length) {
      if (temp.includes(id)) {
        temp = temp.filter((item) => item != id);
      } else {
        temp.push(id);
      }
    } else {
      temp.push(id);
    }
    setSelectedId(temp);
    setKey(listOrdersDelivery.selectedOrders, temp, true);
    setSelected(temp.length == totalRecords);
  };

  /**
   * Creates a new route plan for the specified orders and vehicle.
   *
   * @param {any} orderIds - Array of order IDs to include in the route plan.
   * @param {any} vehicleId - ID of the vehicle to assign to the route plan.
   * @param {any} vehicleLogData - Additional data about the vehicle for logging purposes.
   */
  const planRouteWithVehicles = async (
    orderIds: any,
    vehicleId: any,
    vehicleLogData: any,
  ) => {
    const selectedVehicleOrderIds = (orderIds || []).filter(
      (orderId: any) => typeof orderId === "string" && orderId,
    );
    let params: any = {
      date: Method.convertDateToFormat(state.date, "YYYY-MM-DD"),
      orders: selectedVehicleOrderIds,
      planCreateContext: "EDIT_ROUTE_MANUAL_ASSIGNMENT",
    };
    if (isPlanCreate) {
      error("Route planning is in progress!!Please wait...");
    } else {
      params = { ...params, vehicles: [vehicleId] };
    }
    setLoading(true);
    setProcessableOrders(orderIds);

    const apiResponse = await callApiWithToast({
      endpoint: routePlanning.planCreate,
      params: routePlanningJSON.createPlan(params),
      module: OrdersDelivery,
      warehouse: selectedWarehouse?.value,
    });
    const response = getRecordFromStructuredResponse(apiResponse);

    if (response) {
      setIsPlanCreate(true);
      let temp = [...selectedVehicle];
      temp.push(vehicleId);
      setSelectedVehicle(temp);
      setRouteId(response._id);
      setKey(listOrdersDelivery.selectedOrders, [], true);
    } else {
      setIsPlanCreate(false);
      setShowGeneratePlan(false);
      setLoading(false);
    }
  };
  const handleAddOrderToList = async (
    data: any,
    routeId: any,
    vehicleId: any,
  ) => {
    setLoading(true);
    setIsPlanCreate(true);
    const processedRefKeys = new Set();

    let filteredOrders = data.filter(
      (order: any) => !(order.isExistingOrder && order.partialDelivery),
    );
    const updatedOrders = filteredOrders.map((order: any) => {
      // Check if the refKey has already been processed
      if (!processedRefKeys.has(order.refKey)) {
        // If not, mark this order as not existing and add the refKey to the set
        processedRefKeys.add(order.refKey);
        return {
          ...order,
        };
      } else {
        // If it has been processed, keep isExistingOrder as true
        return {
          ...order,
        };
      }
    });

    // Now filter the orders based on the updated isExistingOrder
    filteredOrders = updatedOrders.filter(
      (order: any) => !(order.isExistingOrder && order.partialDelivery),
    );

    let orders: any = [];
    filteredOrders.map((val: any) => {
      orders.push(val.postData._id);
    });
    let params = {
      order: orders,
    };
    if (orders.length) {
      let apiService = new APICallService(
        routePlanning.addOrder,
        routePlanningJSON.addOrder(params),
        {
          routeId: routeId,
          vehicleId: vehicleId,
        },
        "",
        "",
        "",
        OrdersDelivery,
        selectedWarehouse?.value || null,
      );
      let response: any = await apiService.callAPI();
      if (response) {
        let temp = [...selectedVehicle];
        temp.push(vehicleId);
        setSelectedVehicle(temp);
        setRouteId(routeId);
        setVehicleModalShow(true);
        setVehicleId("");
        setIsPlanCreate(false);
        setShowGeneratePlan(false);
        setLoading(false);
        setKey(listOrdersDelivery.selectedOrders, [], true);
      } else {
        setIsPlanCreate(false);
        setShowGeneratePlan(false);
        setLoading(false);
      }
      setIsPlanCreate(false);
    }
    setIsPlanCreate(false);

    setLoading(false);
  };
  useEffect(() => {
    const checkPlanStatus = async () => {
      if (isPlanCreate && processableOrders.length) {
        let params: any = {
          date: Method.convertDateToFormat(state.date, "YYYY-MM-DD"),
          orders: processableOrders,
          vehicles: state?.selectedVehicle,
        };
        if (routeId) {
          params = { ...params, route: routeId };
        }
        if (selectedWarehouse?.value) {
          params.warehouse = selectedWarehouse?.value;
        }
        const apiResponse = await callApiWithToast({
          endpoint: routePlanning.planSummary,
          params: routePlanningJSON.listPlanSummary(params),
          module: OrdersDelivery,
          warehouse: selectedWarehouse?.value,
        });
        console.trace(apiResponse)
        const response = getRecordFromStructuredResponse(apiResponse);
        if (!response) {
          setIsPlanCreate(false);
          setShowGeneratePlan(false);
          setLoading(false);
          return;
        }
        if (response.status === RoutePlanned) {
          setRoutePlanRefKey(response.refKey || "");
          if (vehicleId) {
            setIsPlanCreate(false);
            if (!selectedVehicle.length) {
              navigate("/orders/review-order-list", {
                state: {
                  routeId: response._id,
                },
              });
            }

            setVehicleModalShow(true);
            setVehicleId("");
            setIsPlanCreate(false);
            setShowGeneratePlan(false);
            setLoading(false);
          } else {
            setIsPlanCreate(false);
            if (!selectedVehicle.length) {
              navigate("/orders/review-order-list", {
                state: {
                  routeId: response._id,
                },
              });
            }
            setVehicleModalShow(true);
            setVehicleId("");
            setIsPlanCreate(false);
            setShowGeneratePlan(false);
            setLoading(false);
          }
        } else if (
          response.status === RoutePlanFailed ||
          response.status === RoutePlanCancelled
        ) {
          error("Plan Generation Failed or Cancelled");
          setIsPlanCreate(false);
          setShowGeneratePlan(false);
          setLoading(false);
        } else {
          setTimeout(checkPlanStatus, 1000); // Call again after 1 second
        }
      }
    };
    if (isPlanCreate == true) {
      checkPlanStatus();
    }
  }, [isPlanCreate]);

  const handleShow = (index: number) => {
    let temp = { ...summary };
    temp.planData.plan.trucksWithOrders[index].isOpen =
      !temp.planData.plan.trucksWithOrders[index].isOpen;
    setSummary(temp);
  };
  const handleDiscardedShow = () => {
    setDiscarded(!discarded);
  };

  useEffect(() => {
    setKey(listOrdersDelivery.selectedOrders, [], true);
  }, [selectedId, selected]);

  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };

  useEffect(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    let nextOrders = applyOrderTypeFilter(orders, orderTypeFilter);

    if (normalizedSearchTerm) {
      nextOrders = nextOrders.filter((order: any) => {
        return (
          String(order.refKey || "")
            .toLowerCase()
            .includes(normalizedSearchTerm) ||
          String(order.customer?.name || order.customerDetails?.name || "")
            .toLowerCase()
            .includes(normalizedSearchTerm)
        );
      });
    }

    setFilteredOrders(nextOrders);
  }, [orders, orderTypeFilter, searchTerm]);

  /** Handles the "Edit Route Orders" click for a vehicle card: loads addable orders. */
  const handleEditRouteOrdersClick = async (
    vehicleVal: any,
    vehicleIndex: number,
  ) => {
    setSelectedVehicleIndex(vehicleIndex);
    setSelectedVehicleId(vehicleVal.postData.vModel.reference);
    setEditRouteModal(true);
    await fetchDetails(
      [1],
      "",
      "",
      "",
      vehicleVal.ward,
      vehicleVal.postData.vModel.reference,
      districtIds[vehicleIndex],
    );
    handleDefaultCheck(vehicleIndex);
  };

  /** Handles "Generate Plan"/"Plan generated" click: creates a route or adds orders to an existing one. */
  const handleGenerateOrAddOrderClick = (vehicleVal: any) => {
    let temp: any = [];
    vehicleVal.routeId
      ? vehicleVal.orders.map((orderVal: any) => {
          temp.push(orderVal);
        })
      : vehicleVal.orders.map((orderVal: any) => {
          temp.push(orderVal.postData._id);
        });
    setVehicleId(vehicleVal.postData.vModel.reference);
    setVehicleData(vehicleVal.postData.vModel);
    vehicleVal.routeId
      ? handleAddOrderToList(
          temp,
          vehicleVal.routeId,
          vehicleVal.postData.vModel.reference,
        )
      : planRouteWithVehicles(
          temp,
          vehicleVal.postData.vModel.reference,
          vehicleVal.postData.vModel,
        );
  };

  /** Builds the warehouse -> stops -> warehouse coordinate list for the route map modal. */
  const handleViewMapClick = (vehicleVal: any) => {
    let temp: any = [
      {
        lat: selectedWarehouse?.lat,
        lng: selectedWarehouse?.lng,
        label: "Warehouse",
        address: selectedWarehouse?.label,
      },
    ];
    vehicleVal.orders.map((orderVal: any) => {
      temp.push({
        lat: orderVal.lat,
        lng: orderVal.lng,
        label: `Order #${orderVal.refKey}`,
        address: formatDeliveryAddress(orderVal.address, {
          showOnlyAddress: true,
        }),
      });
    });
    temp.push({
      lat: selectedWarehouse?.lat,
      lng: selectedWarehouse?.lng,
      label: "Warehouse",
      address: selectedWarehouse?.label,
    });
    setCoordinates(temp);
    setShowRouteDetails(true);
  };

  const handleCloseEditRouteModal = () => {
    setOrders([]);
    setSelectedOrders(0);
    setDiscardedOrder([]);
    setDiscarded(false);
    setEditRouteModal(false);
  };

  const handleSaveEditRoute = () => {
    fetchSummaryForEditRoute(selectedVehicleId, selectedId, selectedVehicleIndex);
    setKey(listOrdersDelivery.selectedOrders, [], true);
  };

  return (
    <>
      {vehicleId || loading ? (
        <LoadingScreen isLoading={isPlanCreate || loading} />
      ) : (
        <></>
      )}
      <div className="p-5">
        <Row className="align-items-center g-md-8 g-6 mb-5">
          <Col xs={12}>
            <Row className="align-items-center g-3">
              <Col xs>
                <h1 className="fs-22 mb-0 fw-bolder">
                  Plan details for {formattedDate}
                </h1>
              </Col>
            </Row>
          </Col>
        </Row>
        {fetchLoader ? (
          <>
            <div className="d-flex justify-content-center">
              <Loader loading={fetchLoader} />
            </div>
          </>
        ) : (
          <>
            {summary && Object.keys(summary).length > 0 ? (
              <Row className="g-7">
                <>
                  <Col xs={12} className="mb-7">
                    <Row className="g-6">
                      <SummaryInfoCard
                        bgClassName="bg-ccebfd"
                        value={analytic.totalTrucks}
                        label="Total Vehicle Used"
                      />
                      <SummaryInfoCard
                        bgClassName="bg-d4e1fc"
                        value={formatWeightTons(analytic.totalConsumedWeight)}
                        label="Total Weight"
                      />
                      <SummaryInfoCard
                        bgClassName="bg-ccf3f0"
                        value={`${analytic.totalOrders} ${
                          analytic.totalOrders === 1 ? "order" : "orders"
                        }`}
                        label="Total orders"
                      />
                    </Row>
                  </Col>
                  {summary.planData.plan.trucksWithOrders.length ? (
                    <>
                      {summary.planData.plan.trucksWithOrders.map(
                        (vehicleVal: any, vehicleIndex: number) => (
                          <VehicleRouteCard
                            key={
                              vehicleVal.postData?.vModel?.reference ??
                              vehicleIndex
                            }
                            vehicleVal={vehicleVal}
                            vehicleIndex={vehicleIndex}
                            selectedVehicle={selectedVehicle}
                            isPlanCreate={isPlanCreate}
                            showRouteDetails={showRouteDetails}
                            coordinates={coordinates}
                            onHideRouteMap={() => {
                              setCoordinates([]);
                              setShowRouteDetails(false);
                            }}
                            onEditRouteOrders={handleEditRouteOrdersClick}
                            onGenerateOrAddOrder={handleGenerateOrAddOrderClick}
                            onViewMap={handleViewMapClick}
                            onToggleOpen={handleShow}
                          />
                        ),
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                  {summary.planData.plan.vehiclesWithoutOrders &&
                  summary.planData.plan.vehiclesWithoutOrders.length ? (
                    <>
                      {summary.planData.plan.vehiclesWithoutOrders.map(
                        (vehicleVal: any, vehicleIndex: number) => (
                          <VehicleWithoutOrdersCard
                            key={
                              vehicleVal.postData?.vModel?.reference ??
                              `no-orders-${vehicleIndex}`
                            }
                            vehicleVal={vehicleVal}
                          />
                        ),
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                </>
              </Row>
            ) : (
              <div className="d-flex justify-content-center">
                <Loader loading={fetchLoader} />
              </div>
            )}
          </>
        )}
      </div>
      <EditRouteOrderModal
        show={editRouteModal}
        onClose={handleCloseEditRouteModal}
        orderTypeFilter={orderTypeFilter}
        onOrderTypeFilterChange={setOrderTypeFilter}
        searchTerm={searchTerm}
        onSearchChange={handleSearch}
        loading={loading}
        orders={orders}
        filteredOrders={filteredOrders}
        visible={visible}
        discardedOrder={discardedOrder}
        selectedOrder={selectedOrder}
        discarded={discarded}
        onToggleDiscarded={handleDiscardedShow}
        modalVehicleAnalytics={modalVehicleAnalytics}
        currentUser={currentUser}
        selectLoading={selectLoading}
        selectedId={selectedId}
        totalRecords={totalRecords}
        onSelectAll={handleSelectAll}
        onChecked={handleChecked}
        onSave={handleSaveEditRoute}
        onSaveLoader={onSaveLoader}
      />
    </>
  );
};
export default DeliveryRoutesPlanDetails;
